import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ToggleButton from '../components/ToggleButton';
import "./ContactPage.css";

import ContactDetails from '../components/ContactDetails';
import ContactSpecifics from '../components/ContactSpecifics';

import { API_BASE } from "../config";

const ContactPage = () => {
    const [joinOrHire, setJoinOrHire] = useState(true); // false = "Hire us", true = "Join us"
    const [currentMobilePage, setCurrentMobilePage] = useState(0); // 0 = ContactDetails, 1-3 = ContactSpecifics pages
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [currentView, setCurrentView] = useState('form'); // 'form', 'review', or 'submitted'
    const [formDataForReview, setFormDataForReview] = useState(null); // Store form data for review
    const [storedFormData, setStoredFormData] = useState(null); // Store form data to restore when going back
    const [contactDetailsData, setContactDetailsData] = useState(null); // Store contact details data

    // Create a ref to access ContactDetails methods
    const contactDetailsRef = React.useRef();

    const handleToggleChange = (isToggled) => {
        setJoinOrHire(isToggled);
    };

    const handleMobilePageChange = (pageIndex) => {
        setCurrentMobilePage(pageIndex);
    };

    // Function to get contact details from ContactDetails component
    const getContactDetails = () => {
        if (contactDetailsRef.current) {
            return contactDetailsRef.current.getContactDetails();
        }
        return contactDetailsData || {};
    };

    const handleReviewForm = (completeFormData) => {
        // Get the latest contact details
        const contactDetails = getContactDetails();

        // Combine the data
        const fullFormData = {
            contactDetails: contactDetails,
            contactSpecifics: completeFormData.contactSpecifics
        };

        // Store the complete form data for review
        setFormDataForReview(fullFormData);
        // Also store the form data for restoration when going back
        setStoredFormData(fullFormData);
        // Switch to review view
        setCurrentView('review');
    };

    // Update handleBackToForm to always go to page 3
    const handleBackToForm = () => {
        setCurrentView('form');
        setCurrentMobilePage(3); // Always go to page 3 (last ContactSpecifics page)
    };

    const handleFormSubmit = async (formData) => {
        setIsSubmitting(true);
        setSubmitMessage('');

        console.log('Submitting form with data:', formData);
        console.log('API_BASE:', API_BASE);
        console.log('Full URL:', `${API_BASE}/contact`);

        // Debug: Log the structure of formData
        console.log('formData.contactDetails:', formData.contactDetails);
        console.log('formData.contactSpecifics:', formData.contactSpecifics);

        try {
            // Create FormData to handle file uploads
            const formDataToSend = new FormData();
            formDataToSend.append('joinOrHire', joinOrHire);

            // Add contact details with flat keys
            Object.keys(formData.contactDetails).forEach(key => {
                if (key === 'files' && formData.contactDetails[key] && formData.contactDetails[key].length > 0) {
                    // Append all files
                    formData.contactDetails[key].forEach((file, index) => {
                        formDataToSend.append(`file_${index}`, file);
                    });
                    // Also append the count
                    formDataToSend.append('fileCount', formData.contactDetails[key].length);
                } else if (key !== 'files') {
                    formDataToSend.append(key, formData.contactDetails[key]);
                }
            });

            // Add contact specifics with flat keys
            Object.keys(formData.contactSpecifics).forEach(key => {
                if (key === 'customSkills') {
                    // Handle custom skills specially - include them in the submission
                    const customSkills = formData.contactSpecifics[key];
                    Object.keys(customSkills).forEach(category => {
                        if (customSkills[category] && customSkills[category].length > 0) {
                            formDataToSend.append(`customSkills_${category}`, JSON.stringify(customSkills[category]));
                        }
                    });
                } else if (Array.isArray(formData.contactSpecifics[key])) {
                    formDataToSend.append(key, JSON.stringify(formData.contactSpecifics[key]));
                } else {
                    formDataToSend.append(key, formData.contactSpecifics[key]);
                }
            });

            // Debug: Log what we're sending
            console.log('FormData contents:');
            for (let [key, value] of formDataToSend.entries()) {
                console.log(`${key}:`, value);
            }

            const response = await fetch(`${API_BASE}/contact`, {
                method: 'POST',
                body: formDataToSend
                // Note: Don't set Content-Type header when using FormData
                // The browser will automatically set it with the boundary
            });

            console.log('Response status:', response.status);
            console.log('Response headers:', response.headers);

            const result = await response.json();
            console.log('Response data:', result);

            if (response.ok) {
                setSubmitMessage(result.message);
                setCurrentView('submitted'); // Switch to submitted view
                // Reset form or redirect
            } else {
                setSubmitMessage(result.error || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitMessage('Network error. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Add a review component
    const ReviewView = ({ formData, onBack, onSubmit }) => {
        if (!formData) return null;

        const handleSubmit = () => {
            onSubmit(formData);
        };

        // Helper function to format file size
        const formatFileSize = (bytes) => {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        };

        // Helper function to get custom skills for a category
        const getCustomSkills = (category) => {
            return formData.contactSpecifics.customSkills?.[category] || [];
        };

        return (
            <>
                <div className="review-header-outside">
                    <h1>Review Your Application</h1>
                    <p className="review-subtitle">
                        Please review your information before submitting
                    </p>
                </div>

                <div className="review-container">
                    <div className="review-content">
                        <div className="review-section">
                            <div className="section-header">
                                <h2>Contact Information</h2>
                            </div>
                            <div className="review-grid">
                                <div className="review-item">
                                    <label>{joinOrHire ? "Your Name" : "Company/Contact Name"}</label>
                                    <span className="review-value">{formData.contactDetails.name}</span>
                                </div>
                                <div className="review-item">
                                    <label>Email</label>
                                    <span className="review-value">{formData.contactDetails.email}</span>
                                </div>
                                <div className="review-item">
                                    <label>Phone</label>
                                    <span className="review-value">{formData.contactDetails.phone}</span>
                                </div>
                                {formData.contactDetails.files && formData.contactDetails.files.length > 0 && (
                                    <div className="review-item">
                                        <label>{joinOrHire ? "Attachments (CV, portfolio, etc.)" : "Project Documents"}</label>
                                        <div className="review-files">
                                            {formData.contactDetails.files.map((file, index) => (
                                                <div key={index} className="file-item">
                                                    <span className="file-name">{file.name}</span>
                                                    <span className="file-size">({formatFileSize(file.size)})</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="review-section">
                            <div className="section-header">
                                <h2>{joinOrHire ? "Application Details" : "Project Details"}</h2>
                            </div>
                            <div className="review-grid">
                                {joinOrHire ? (
                                    // Join us fields
                                    <>
                                        {formData.contactSpecifics.expertise && formData.contactSpecifics.expertise.length > 0 && (
                                            <div className="review-item">
                                                <label>Expertise</label>
                                                <div className="review-tags">
                                                    {formData.contactSpecifics.expertise.map((item, index) => (
                                                        <span key={index} className="tag">{item}</span>
                                                    ))}
                                                    {getCustomSkills('expertise').map((item, index) => (
                                                        <span key={`custom-${index}`} className="tag tag-custom">{item}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {formData.contactSpecifics.languages && formData.contactSpecifics.languages.length > 0 && (
                                            <div className="review-item">
                                                <label>Programming Languages</label>
                                                <div className="review-tags">
                                                    {formData.contactSpecifics.languages.map((item, index) => (
                                                        <span key={index} className="tag">{item}</span>
                                                    ))}
                                                    {getCustomSkills('languages').map((item, index) => (
                                                        <span key={`custom-${index}`} className="tag tag-custom">{item}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {formData.contactSpecifics.experience && formData.contactSpecifics.experience.length > 0 && (
                                            <div className="review-item">
                                                <label>Experience Level</label>
                                                <div className="review-tags">
                                                    {formData.contactSpecifics.experience.map((item, index) => (
                                                        <span key={index} className="tag">{item}</span>
                                                    ))}
                                                    {getCustomSkills('experience').map((item, index) => (
                                                        <span key={`custom-${index}`} className="tag tag-custom">{item}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {formData.contactSpecifics.education && formData.contactSpecifics.education.length > 0 && (
                                            <div className="review-item">
                                                <label>Education</label>
                                                <div className="review-tags">
                                                    {formData.contactSpecifics.education.map((item, index) => (
                                                        <span key={index} className="tag">{item}</span>
                                                    ))}
                                                    {getCustomSkills('education').map((item, index) => (
                                                        <span key={`custom-${index}`} className="tag tag-custom">{item}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {formData.contactSpecifics.description && (
                                            <div className="review-item full-width">
                                                <label>Description</label>
                                                <div className="review-text">{formData.contactSpecifics.description}</div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    // Hire us fields
                                    <>
                                        {formData.contactSpecifics.projectType && formData.contactSpecifics.projectType.length > 0 && (
                                            <div className="review-item">
                                                <label>Project Type</label>
                                                <div className="review-tags">
                                                    {formData.contactSpecifics.projectType.map((item, index) => (
                                                        <span key={index} className="tag">{item}</span>
                                                    ))}
                                                    {getCustomSkills('projectType').map((item, index) => (
                                                        <span key={`custom-${index}`} className="tag tag-custom">{item}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {formData.contactSpecifics.languages && formData.contactSpecifics.languages.length > 0 && (
                                            <div className="review-item">
                                                <label>Technologies Involved</label>
                                                <div className="review-tags">
                                                    {formData.contactSpecifics.languages.map((item, index) => (
                                                        <span key={index} className="tag">{item}</span>
                                                    ))}
                                                    {getCustomSkills('languages').map((item, index) => (
                                                        <span key={`custom-${index}`} className="tag tag-custom">{item}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {formData.contactSpecifics.description && (
                                            <div className="review-item full-width">
                                                <label>Project Description</label>
                                                <div className="review-text">{formData.contactSpecifics.description}</div>
                                            </div>
                                        )}
                                        {formData.contactSpecifics.timeline && formData.contactSpecifics.timeline.length > 0 && (
                                            <div className="review-item">
                                                <label>Timeline</label>
                                                <div className="review-tags">
                                                    {formData.contactSpecifics.timeline.map((item, index) => (
                                                        <span key={index} className="tag">{item}</span>
                                                    ))}
                                                    {getCustomSkills('timeline').map((item, index) => (
                                                        <span key={`custom-${index}`} className="tag tag-custom">{item}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {formData.contactSpecifics.budget && formData.contactSpecifics.budget.length > 0 && (
                                            <div className="review-item">
                                                <label>Budget Range</label>
                                                <div className="review-tags">
                                                    {formData.contactSpecifics.budget.map((item, index) => (
                                                        <span key={index} className="tag">{item}</span>
                                                    ))}
                                                    {getCustomSkills('budget').map((item, index) => (
                                                        <span key={`custom-${index}`} className="tag tag-custom">{item}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="review-actions">
                        <button
                            type="button"
                            onClick={onBack}
                            className="review-btn review-btn-secondary"
                            disabled={isSubmitting}
                        >
                            ← Back to Edit
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="review-btn review-btn-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : (joinOrHire ? 'Submit Application' : 'Submit Project Request')}
                        </button>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="page-wrapper contact-page-wrapper">
            <Header />

            <div className="page-content">
                <div className="page-container">
                    <div className="container">
                        {/* Only show toggle button when not in review mode */}
                        {currentView !== 'review' && currentView !== 'submitted' && (
                            <>
                                <h1>Contact</h1>
                                <ToggleButton
                                    text1="Join us"
                                    text2="Hire us"
                                    selected={joinOrHire}
                                    onChange={handleToggleChange}
                                />
                            </>
                        )}

                        {currentView === 'submitted' ? (
                            <div className="submitted-message">
                                <h2 style={{ textAlign: 'center' }}>{submitMessage}</h2>
                                <p>We've received your inquiry and will get back to you as soon as possible.</p>
                                <p>In the meantime, feel free to explore our website or reach out to us directly.</p>
                            </div>
                        ) : currentView === 'review' ? (
                            <ReviewView
                                formData={formDataForReview}
                                onBack={handleBackToForm}
                                onSubmit={handleFormSubmit}
                            />
                        ) : (
                            <div className="contact-form">
                                <ContactDetails
                                    ref={contactDetailsRef}
                                    joinOrHire={joinOrHire}
                                    currentMobilePage={currentMobilePage}
                                    onMobilePageChange={handleMobilePageChange}
                                    onSubmit={handleFormSubmit}
                                    isSubmitting={isSubmitting}
                                    storedFormData={storedFormData} // Pass stored data for restoration
                                />
                                <ContactSpecifics
                                    joinOrHire={joinOrHire}
                                    currentMobilePage={currentMobilePage}
                                    onMobilePageChange={handleMobilePageChange}
                                    onSubmit={handleFormSubmit} // This is for the final submission
                                    onReview={handleReviewForm} // This is for the review step
                                    isSubmitting={isSubmitting}
                                    storedFormData={storedFormData} // Pass stored data for restoration
                                    getContactDetails={getContactDetails} // Pass the function to get contact details
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ContactPage;
